/**
 * Utility functions for table filtering, searching and sorting
 * Used with @tanstack/react-table to handle complex data structures
 */

import {
  isNil,
  isObject,
  isArray,
  reduce,
  flow,
  toLower,
  some,
  isNumber,
  toString,
  isEmpty,
} from 'lodash-es';

export const extractSearchableValue = (value: any): string => {
  if (isNil(value)) return '';

  if (isObject(value)) {
    if (isArray(value)) {
      return value.map(extractSearchableValue).join(' ');
    }

    const allSearchableText = reduce(
      value,
      (acc, propValue) => {
        if (typeof propValue === 'string' || isNumber(propValue)) {
          return `${acc} ${toString(propValue)}`;
        }

        if (!isNil(propValue) && isObject(propValue)) {
          try {
            return `${acc} ${extractSearchableValue(propValue)}`;
          } catch (e) {
            return acc;
          }
        }

        return acc;
      },
      ''
    );

    if (!isEmpty(allSearchableText.trim())) {
      return allSearchableText;
    }

    try {
      return JSON.stringify(value);
    } catch (e) {
      return '';
    }
  }

  return toString(value);
};

export const columnFilterFn = (
  row: any,
  columnId: string,
  filterValue: string
) => {
  const processValue = flow([
    (r: any) => r.getValue(columnId),
    extractSearchableValue,
    toLower,
  ]);

  return processValue(row).includes(filterValue.toLowerCase());
};

export const globalFilterFn = (
  row: any,
  columnId: string,
  filterValue: string
) => {
  return some(row.getAllCells(), (column) => {
    const cellValue = (column as { getValue: () => any }).getValue();
    const searchableText = extractSearchableValue(cellValue).toLowerCase();
    return searchableText.includes(filterValue.toLowerCase());
  });
};

export const objectValueSorting = (
  rowA: any,
  rowB: any,
  columnId: string
): number => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  const isAEmpty = isNil(a);
  const isBEmpty = isNil(b);
  if (isAEmpty || isBEmpty) {
    if (isAEmpty && isBEmpty) return 0;
    return isAEmpty ? 1 : -1;
  }

  const getNumericValue = (val: any): { value: number; isValid: boolean } => {
    if (isNumber(val)) {
      return { value: val, isValid: !isNaN(val) };
    }
    if (isObject(val) && !isNil(val) && 'value' in val) {
      const num = Number(val?.value ?? 0);
      return { value: num, isValid: !isNaN(num) };
    }
    const num = Number(val || 0);
    return { value: num, isValid: !isNaN(num) };
  };

  const hasNumericValue =
    isNumber(a) ||
    isNumber(b) ||
    (isObject(a) && !isNil(a) && 'value' in a) ||
    (isObject(b) && !isNil(b) && 'value' in b);

  if (hasNumericValue) {
    const numA = getNumericValue(a);
    const numB = getNumericValue(b);

    if (!numA.isValid && !numB.isValid) return 0;
    if (!numA.isValid) return 1;
    if (!numB.isValid) return -1;

    return numA.value - numB.value;
  }

  if (isObject(a) && !isNil(a) && isObject(b) && !isNil(b)) {
    const nameFields = [
      'entityName',
      'workspaceName',
      'burdenedEntityName',
      'name',
    ];

    // Use optional chaining to avoid type errors
    for (const field of nameFields) {
      if (!isNil(a) && !isNil(b) && field in a && field in b) {
        const aValue = toString((a as Record<string, any>)[field] || '');
        const bValue = toString((b as Record<string, any>)[field] || '');

        return aValue.localeCompare(bValue, undefined, { numeric: true });
      }
    }

    return JSON.stringify(a).localeCompare(JSON.stringify(b), undefined, {
      numeric: true,
    });
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { numeric: true });
  }

  if (isNumber(a) && isNumber(b)) {
    return a - b;
  }

  return toString(a).localeCompare(toString(b), undefined, {
    numeric: true,
  });
};
